import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Card,
  Button,
  Icon,
  Divider,
  InputSelect,
  Form,
  InputText,
  InputDatePicker
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { HomeDto } from '@lmc/ts-common'
import { ConsentStatus } from '@lmc/common/types'
import { getNominative } from 'common/utils'

const STATUS_INFO_TEXT_KEYS: Record<ConsentStatus, string> = {
  [ConsentStatus.noStatus]: 'infoText.noStatus',
  [ConsentStatus.consentNotObtained]: 'infoText.consentNotObtained',
  [ConsentStatus.completed]: 'infoText.completed',
  [ConsentStatus.planned]: 'infoText.planned',
  [ConsentStatus.requested]: 'infoText.requested',
  [ConsentStatus.scheduled]: 'infoText.scheduled',
  [ConsentStatus.postponed]: 'infoText.postponed',
  [ConsentStatus.cancelled]: 'infoText.cancelled',
  [ConsentStatus.withdrawn]: 'infoText.withdrawn',
  [ConsentStatus.refused]: 'infoText.refused',
  [ConsentStatus.provided]: 'infoText.provided'
}

type StatusOptionConfig = {
  labelKey?: string
  options: {
    labelKey: string
    value: ConsentStatus
  }[]
}

const STATUS_OPTION_GROUPS: StatusOptionConfig[] = [
  {
    options: [
      {
        labelKey: 'options.noStatus',
        value: ConsentStatus.noStatus
      }
    ]
  },
  {
    labelKey: 'options.saveForm',
    options: [
      {
        labelKey: 'options.consentNotObtained',
        value: ConsentStatus.consentNotObtained
      },
      {
        labelKey: 'options.completed',
        value: ConsentStatus.completed
      },
      {
        labelKey: 'options.planned',
        value: ConsentStatus.planned
      },
      {
        labelKey: 'options.requested',
        value: ConsentStatus.requested
      },
      {
        labelKey: 'options.scheduled',
        value: ConsentStatus.scheduled
      },
      {
        labelKey: 'options.postponed',
        value: ConsentStatus.postponed
      }
    ]
  },
  {
    labelKey: 'options.signForm',
    options: [
      {
        labelKey: 'options.cancelled',
        value: ConsentStatus.cancelled
      },
      {
        labelKey: 'options.withdrawn',
        value: ConsentStatus.withdrawn
      },
      {
        labelKey: 'options.refused',
        value: ConsentStatus.refused
      },
      {
        labelKey: 'options.provided',
        value: ConsentStatus.provided
      }
    ]
  }
]

export type GroupedSelectOption = {
  label: string
  value: string
}

export type GroupedSelectOptions = {
  label: string
  options: GroupedSelectOption[]
}[]

export const SIGN_FORM_STATUSES: ConsentStatus[] = [
  ConsentStatus.cancelled,
  ConsentStatus.withdrawn,
  ConsentStatus.refused,
  ConsentStatus.provided
]

export const NEXT_REVIEW_DISABLED_STATUSES: ConsentStatus[] = [
  ConsentStatus.cancelled,
  ConsentStatus.consentNotObtained,
  ConsentStatus.withdrawn,
  ConsentStatus.completed,
  ConsentStatus.noStatus
]

export const NEXT_REVIEW_OPTIONAL_STATUSES: ConsentStatus[] = [
  ConsentStatus.refused,
  ConsentStatus.provided
]

export const getNextReviewFieldState = (status: ConsentStatus) => {
  const isDisabled = NEXT_REVIEW_DISABLED_STATUSES.includes(status)
  const isOptional = NEXT_REVIEW_OPTIONAL_STATUSES.includes(status)
  const isRequired = !isDisabled && !isOptional

  return { isDisabled, isOptional, isRequired }
}

export const getIsConsentConfirmDisabled = ({
  status,
  reason,
  disabled = false
}: {
  status: ConsentStatus
  reason?: string
  lastReviewDate?: string | null
  disabled?: boolean
}): boolean => {
  return (
    status === ConsentStatus.noStatus ||
    disabled ||
    (status === ConsentStatus.withdrawn && !reason?.trim())
  )
}

export const getIsSignStatus = (status: ConsentStatus): boolean => {
  return SIGN_FORM_STATUSES.includes(status)
}

type ConsentConfirmationCardProps = {
  onSubmit?: () => void
  onStatusChange?: ({
    status,
    reason,
    lastReviewDate
  }: {
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }) => void
  selectedStatus: {
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }
  testId?: string
  disabled?: boolean
  signButtonLabel?: string
  confirmButtonLabel?: string
}

const ConsentConfirmationCard: React.FC<ConsentConfirmationCardProps> = ({
  onSubmit,
  onStatusChange,
  selectedStatus,
  testId,
  disabled = false,
  signButtonLabel,
  confirmButtonLabel
}) => {
  const { t } = useTranslation()

  const translate = (key: string, variable?: any) =>
    t(
      `consent.createConsentForms.components.consentConfirmationCard.${key}`,
      variable
    )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: HomeDto } }) =>
      state.global.home.preferredPeopleNominative
  )

  const preferredNominative = getNominative(
    preferredPeopleNominative,
    'resident'
  ).toLocaleLowerCase()

  const [internalStatus, setInternalStatus] = useState<ConsentStatus>(
    ConsentStatus.noStatus
  )
  const [lastReviewDate, setLastReviewDate] = useState<string | null>(null)
  const [reason, setReason] = useState<string>('')

  const currentStatus = selectedStatus ?? { status: internalStatus }

  const isSignStatus = getIsSignStatus(currentStatus.status)

  const handleStatusChange = (value: string) => {
    setReason('')
    setLastReviewDate(null)
    const statusValue = value as ConsentStatus
    setInternalStatus(statusValue)

    if (onStatusChange) {
      onStatusChange({
        status: statusValue,
        reason,
        lastReviewDate: lastReviewDate as string
      })
    }
  }

  const getInfoText = (): string => {
    const safeStatus = currentStatus?.status ?? ConsentStatus.noStatus
    const infoKey = STATUS_INFO_TEXT_KEYS[safeStatus]
    return translate(infoKey)
  }

  const baseOptions =
    STATUS_OPTION_GROUPS.find((group) => !group.labelKey)?.options ?? []

  const groupedOptionConfigs = STATUS_OPTION_GROUPS.filter(
    (group) => !!group.labelKey
  )

  const options = baseOptions.map((option) => ({
    label: translate(option.labelKey),
    value: option.value
  }))

  const groupedOptions: GroupedSelectOptions = groupedOptionConfigs.map(
    (group) => ({
      label: group.labelKey ? translate(group.labelKey) : '',
      options: group.options.map((option) => ({
        label: translate(option.labelKey),
        value: option.value
      }))
    })
  )

  const getButtonLabel = (): string => {
    if (isSignStatus) {
      return signButtonLabel ?? translate('signButton')
    }
    return confirmButtonLabel ?? translate('confirmButton')
  }

  return (
    <StyledCard
      data-testid={
        testId
          ? `${testId}-consent-confirmation-card`
          : 'consent-confirmation-card'
      }
    >
      {onSubmit && (
        <>
          <TopSection>
            <QuestionText level={1}>
              {translate('question', { nominative: preferredNominative })}
            </QuestionText>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={disabled}
              data-testid={
                testId
                  ? `${testId}-confirm-button`
                  : 'consent-confirmation-confirm-button'
              }
            >
              <Icon name="CheckOutlined" size={16} />
              <Space>{getButtonLabel()}</Space>
            </Button>
          </TopSection>

          <StyledDivider />
        </>
      )}

      <StatusSection>
        <LabelContainer>
          <LabelText level={1}>{translate('consentStatusLabel')}</LabelText>
          <Asterisk>*</Asterisk>
        </LabelContainer>

        <HelperText level={2}>{translate('helperText')}</HelperText>

        <SelectContainer>
          <Form>
            <StyledInputSelect
              testId={
                testId
                  ? `${testId}-consent-status-select`
                  : 'consent-status-select'
              }
              options={options}
              value={currentStatus.status}
              onChange={handleStatusChange}
              groupedOptions={groupedOptions}
              layout="column"
              defaultValue={ConsentStatus.noStatus}
              formItemstyle={selectFormItemStyle}
              listHeight={350}
            />
          </Form>
        </SelectContainer>
        <InfoText>{getInfoText()}</InfoText>

        {currentStatus?.status === ConsentStatus.postponed && (
          <ExtraSection>
            <LabelContainer>
              <LabelText level={1}>
                {translate('extra.postponed.label')}
              </LabelText>
            </LabelContainer>
            <HelperText level={2}>
              {translate('extra.postponed.helper')}
            </HelperText>
            <StyledInputDatePicker
              onChange={(date: unknown) => {
                const dateValue = date as string | null
                setLastReviewDate(dateValue)
                if (onStatusChange) {
                  onStatusChange({
                    status: currentStatus.status,
                    reason: currentStatus.reason,
                    lastReviewDate: dateValue
                  })
                }
              }}
              wrapFormItem={false}
              value={currentStatus.lastReviewDate || null}
              localization={{
                placeholder: translate('extra.postponed.placeholder'),
                errorMessage: ''
              }}
            />
          </ExtraSection>
        )}

        {currentStatus?.status === ConsentStatus.withdrawn && (
          <ExtraSection>
            <LabelContainer>
              <LabelText level={1}>
                {translate('extra.withdrawn.label')}
              </LabelText>
              <Asterisk>*</Asterisk>
            </LabelContainer>
            <HelperText level={2}>
              {translate('extra.withdrawn.helper')}
            </HelperText>
            <StyledInputText
              onChange={({ target }) => {
                setReason(target.value)
                if (onStatusChange) {
                  onStatusChange({
                    status: currentStatus.status,
                    reason: target.value,
                    lastReviewDate: currentStatus.lastReviewDate
                  })
                }
              }}
              value={currentStatus.reason || ''}
              layout="column"
              placeholder={translate('extra.withdrawn.placeholder')}
            />
          </ExtraSection>
        )}
      </StatusSection>
    </StyledCard>
  )
}

const StyledCard = styled(Card)`
  padding: 24px;
`

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const Space = styled.span`
  margin-left: 8px;
`

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
`

const LabelContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
`

const Asterisk = styled.span`
  color: red;
  margin-left: 2px;
`

const SelectContainer = styled.div``

const ExtraSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const StyledInputText = styled(InputText)`
  width: 390px;
  height: 36px;
`

const StyledInputDatePicker = styled(InputDatePicker)`
  width: 390px;
  .ant-picker {
    width: 100%;
  }
`

const QuestionText = styled(Text)`
  font-size: 24px;
  font-weight: 500;
`

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const LabelText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
`

const HelperText = styled(Text)`
  font-size: 12px;
  margin-bottom: 16px;
`

const StyledInputSelect = styled(InputSelect)`
  width: 390px;
  height: 36px;
`

const InfoText = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.color.neutral.color4};
  max-width: 390px;
  word-wrap: break-word;
  white-space: normal;
`

const selectFormItemStyle = {
  marginBottom: 0
}

export default ConsentConfirmationCard
